import type { ReactNode } from 'react'
import { Check, RefreshCw } from 'lucide-react'
import clsx from 'clsx'
import { SizeBadge } from '@/components/common/SizeBadge'
import { fromAzureLocation } from '@/config/azureRegions'
import { SKU_LABELS, WORKWISE_SKUS, isWindowsOs, osIdForAdminOsType } from '@/config/workwise'
import styles from '@/pages/NewDeploymentPage.module.css'
import type { TShirtSize } from '@/types'
import type { WorkwiseSizePrices } from '@/types/workwise'
import { formatMaxUsers } from '@/utils/maxUsers'
import { type AdminSizes, computeMonthlyOf, formatMonthly, maxUsersOf } from '@/utils/workwise/adminSizes'
import { PRICE_UNAVAILABLE } from '@/utils/workwise/costEstimate'

interface WorkwiseSizeGridProps {
  adminSizes: AdminSizes
  /** GET /workwise/size-prices/ (revision 7); undefined while loading or when it failed. */
  sizePrices?: WorkwiseSizePrices
  pricesStatus?: 'loading' | 'ready' | 'error'
  selected: TShirtSize
  onSelect: (size: TShirtSize) => void
}

const OS_LABEL: Record<string, string> = { windows: 'Windows', linux: 'Linux' }
const PRICE_HINT = 'VM compute, Azure retail, Hybrid Benefit'

/**
 * The OS family of the size: the admin os_type when the API exposes it, else the
 * family of the OS the app server then defaults to (Windows Server).
 */
function osLabel(osType: string | null): string {
  if (osType) return OS_LABEL[osType.toLowerCase()] ?? osType
  return isWindowsOs(osIdForAdminOsType(null)) ? 'Windows' : 'Linux'
}

function PriceHint({ sizePrices, pricesStatus }: Pick<WorkwiseSizeGridProps, 'sizePrices' | 'pricesStatus'>) {
  let content: ReactNode
  if (pricesStatus === 'error') content = `${PRICE_UNAVAILABLE}: Azure retail prices could not be loaded; every size can still be chosen.`
  else if (!sizePrices) {
    content = (
      <>
        <RefreshCw size={11} className={styles.spin} /> Fetching Azure retail prices…
      </>
    )
  } else {
    content = `Est. monthly: ${PRICE_HINT} (${fromAzureLocation(sizePrices.region)}). Disks, the SQL Server licence and shared servers are added to the estimate in Infrastructure & Settings.`
  }
  return (
    <div className={styles.fieldHint} style={{ marginTop: 10 }} data-testid="workwise-size-price-hint">
      {content}
    </div>
  )
}

/**
 * Step 2 size cards for WorkWise: the SAME markup and classes as every other
 * product's cards, filled from the admin T-shirt sizes of the `workwise`
 * product. Revision 7: "Users" (max users of the size) and "Est. monthly" from
 * GET /workwise/size-prices/ (VM compute with Azure Hybrid Benefit). The chosen
 * size is the app server size.
 */
export function WorkwiseSizeGrid({ adminSizes, sizePrices, pricesStatus, selected, onSelect }: WorkwiseSizeGridProps) {
  return (
    <>
      <div className={styles.sizeGrid} data-testid="workwise-size-grid">
        {WORKWISE_SKUS.map((size) => {
          const config = adminSizes[size]
          if (!config) return null
          const isSelected = selected === size
          return (
            <button
              key={size}
              className={clsx(styles.sizeCard, isSelected && styles.sizeCardSelected)}
              onClick={() => onSelect(size)}
              data-testid={`workwise-size-${size}`}
            >
              <div className={styles.sizeCardTop}>
                <SizeBadge size={size} />
                <span className={styles.sizeName}>{SKU_LABELS[size]}</span>
                {isSelected && <Check size={14} className={styles.sizeCheck} />}
              </div>
              <div className={styles.sizeDetails}>
                <div className={styles.sizeDetail}>
                  <span>Users</span>
                  <span data-testid={`workwise-size-${size}-users`}>{formatMaxUsers(maxUsersOf(size, adminSizes, sizePrices))}</span>
                </div>
                <div className={styles.sizeDetail}>
                  <span>VM size</span>
                  <span className={styles.skuTag}>{config.vmSize || '—'}</span>
                </div>
                <div className={styles.sizeDetail}>
                  <span>vCPU</span>
                  <span>{config.vcpu || '—'}</span>
                </div>
                <div className={styles.sizeDetail}>
                  <span>RAM</span>
                  <span>{config.ram || '—'}</span>
                </div>
                <div className={styles.sizeDetail}>
                  <span>Storage</span>
                  <span>{config.storageGb ? `${config.storageGb} GB` : '—'}</span>
                </div>
                <div className={styles.sizeDetail}>
                  <span>OS</span>
                  <span>{osLabel(config.osType)}</span>
                </div>
                <div className={clsx(styles.sizeDetail, styles.sizeCost)} title={PRICE_HINT}>
                  <span>Est. monthly</span>
                  <span data-testid={`workwise-size-${size}-cost`}>{formatMonthly(computeMonthlyOf(size, sizePrices))}</span>
                </div>
              </div>
            </button>
          )
        })}
      </div>
      <PriceHint sizePrices={sizePrices} pricesStatus={pricesStatus} />
    </>
  )
}
